// Step 1 — Welcome / Pitch (US-016, US-017).
//
// Pixel-faithful to `prototypes_and_docs/ls-onboarding-v2.jsx:110-152`. The
// three feature cards live inside the OnboardingStep `body` slot; the CTA is
// a full-width primary Button.

import { ArrowRight, Inbox, Rocket, Target } from 'lucide-react';
import type { ReactNode } from 'react';

import { useT } from '@barel/i18n';
import { Button, Card } from '@barel/ui';
import { OnboardingStep } from '@barel/ui';

interface WelcomeProps {
  onContinue: () => void;
}

interface FeatureCard {
  key: 'campaigns' | 'inbox' | 'signals';
  icon: ReactNode;
}

const FEATURES: FeatureCard[] = [
  { key: 'campaigns', icon: <Rocket aria-hidden style={{ width: 16, height: 16 }} /> },
  { key: 'inbox', icon: <Inbox aria-hidden style={{ width: 16, height: 16 }} /> },
  { key: 'signals', icon: <Target aria-hidden style={{ width: 16, height: 16 }} /> },
];

export function Welcome({ onContinue }: WelcomeProps) {
  const t = useT('onboarding');
  const total = 6;

  return (
    <OnboardingStep
      width="sm"
      eyebrow={t('step1.eyebrow')}
      title={
        <>
          {t('step1.titleLine1')}
          <br />
          <span className="text-accent">{t('step1.titleLine2')}</span>
        </>
      }
      description={t('step1.subtitle')}
      primary={
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={onContinue}
          iconRight={<ArrowRight aria-hidden style={{ width: 14, height: 14 }} />}
          data-testid="onboarding-step1-continue"
        >
          {t('step1.cta')}
        </Button>
      }
      secondary={<span className="text-2xs text-tertiary">{t('step1.footer', { total })}</span>}
    >
      <ul className="flex flex-col gap-2 list-none p-0 m-0">
        {FEATURES.map((feat) => (
          <li key={feat.key} className="m-0">
            <Card padding="md" radius="lg" data-feature={feat.key}>
              <div className="flex gap-3 items-start">
                <span
                  aria-hidden
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-dim text-accent"
                >
                  {feat.icon}
                </span>
                <div className="flex flex-1 flex-col gap-1">
                  <span className="text-sm font-bold text-primary">
                    {t(`step1.features.${feat.key}.title`)}
                  </span>
                  <span className="text-xs leading-relaxed text-secondary">
                    {t(`step1.features.${feat.key}.body`)}
                  </span>
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </OnboardingStep>
  );
}
